import { css } from "@stitches/react";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { KeyboardEvent as ReactKeyboardEvent, useRef } from "react";
import { useTranslation } from "react-i18next";

dayjs.extend(utc);
dayjs.extend(timezone);
// antd parses a typed date with dayjs, which needs this plugin to honour the
// display format (DD/MM/YYYY) instead of falling back to Date parsing.
dayjs.extend(customParseFormat);

/** Day, month and year tokens joined by separators: DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD. */
const MASKABLE_DATE_FORMAT = /^(YYYY|MM|DD)([^A-Za-z0-9]+(YYYY|MM|DD))*$/;

type Enumerate<
  N extends number,
  Acc extends number[] = []
> = Acc["length"] extends N
  ? Acc[number]
  : Enumerate<N, [...Acc, Acc["length"]]>;

export type IntRange<F extends number, T extends number> = Exclude<
  Enumerate<T>,
  Enumerate<F>
>;

type DateTimePickerInputProps = {
  /** Ties the input to a `<label htmlFor>`. */
  id?: string;
  value?: Dayjs | null;
  defaultValue?: Dayjs;
  format?: string;
  onChange(dateTime: Dayjs, dateTimeString: string | string[]): void;
  width?: string;
  hideNowButton?: boolean;
  minuteStep?: IntRange<1, 59>;
  placeholder?: string;
  disabled?: boolean;
  minDate?: Dayjs;
  /** Days after this one are disabled (e.g. no future date of birth). */
  maxDate?: Dayjs;
  showTime?: boolean;
  defaultTime?: Dayjs;
  /** Defaults to the 42px of the portal's form fields. */
  height?: string;
};

export function DateTimePickerInput({
  id,
  value,
  defaultValue,
  format,
  onChange,
  width,
  hideNowButton,
  minuteStep,
  placeholder,
  disabled,
  minDate,
  maxDate,
  showTime = true,
  defaultTime,
  height = "42px",
}: DateTimePickerInputProps) {
  const { t } = useTranslation();

  // Get user's timezone for display
  const userTimezone = dayjs.tz.guess();
  const timezoneAbbr = dayjs().tz(userTimezone).format("z");

  // Get full timezone name (e.g., "Europe/Dublin")
  const timezoneName = userTimezone.split("/").pop()?.replace(/_/g, " ") || "";

  // Create descriptive timezone label
  const timezoneLabel = `${timezoneAbbr} - ${timezoneName}`;

  // Use placeholder as provided, or default without timezone
  // Timezone will be shown in the popup and in the selected value
  const enhancedPlaceholder = placeholder || t("common_selectDateTime");

  // Force 24-hour format for all locales and ensure format consistency
  // If format is provided, replace any 12-hour indicators with 24-hour
  let displayFormat = format || "YYYY-MM-DD HH:mm";
  if (format) {
    // Replace 12-hour format indicators (h, hh, A, a) with 24-hour (HH)
    displayFormat = format
      .replace(/\bh{1,2}\b/g, "HH") // Replace h or hh with HH
      .replace(/\s*[Aa]\s*/g, ""); // Remove AM/PM indicators
  }

  // Add timezone abbreviation to the display format — but only when a time is
  // actually being picked. A bare calendar date has no timezone to report, and
  // labelling one "[IST]" says something untrue about the value.
  const displayFormatWithTz = showTime
    ? `${displayFormat} [${timezoneAbbr}]`
    : displayFormat;

  // EN-5283: a date-only field types as a mask, so the separators fill themselves in and
  // "03102026" reads as 03/10/2026. antd's mask only knows the numeric tokens, which is why
  // pickers with a time (and their timezone suffix) keep typing free-form.
  const maskFormat =
    !showTime && MASKABLE_DATE_FORMAT.test(displayFormat)
      ? { format: displayFormat, type: "mask" as const }
      : undefined;

  // antd's mask ignores a typed separator and only leaves a part once it is full, so
  // "1/10/1990" put "11" in the day and landed on 11/01/0990: a valid date, and a wrong one,
  // that a date of birth field would accept. A separator typed into a part that is only half
  // filled moves on instead (the mask's ArrowRight, keeping the "01" it has padded the day to).
  // A full part has already moved on by itself, so a separator after it does nothing.
  //
  // The mask's position is internal, so it is mirrored here: which part is active and how many
  // digits went into it. When the browser shows a whole part selected (the mask selects the
  // active part, e.g. after a click), that wins.
  const maskSeparators = displayFormat.replace(/[A-Za-z0-9]/g, "");
  const maskParts = Array.from(displayFormat.matchAll(/YYYY|MM|DD/g), (m) => ({
    start: m.index ?? 0,
    length: m[0].length,
  }));
  const maskPosition = useRef({ part: 0, digits: 0 });

  const handleMaskFocus = () => {
    maskPosition.current = { part: 0, digits: 0 };
  };

  const handleMaskKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (!maskFormat) return;
    const input = event.currentTarget as HTMLInputElement;
    const { selectionStart, selectionEnd } = input;
    const lastPart = maskParts.length - 1;
    const position = maskPosition.current;

    const selectedPart = maskParts.findIndex(
      (p) => p.start === selectionStart && p.start + p.length === selectionEnd
    );
    if (selectedPart >= 0 && selectedPart !== position.part) {
      maskPosition.current = { part: selectedPart, digits: 0 };
    }

    const moveTo = (part: number) => {
      maskPosition.current = {
        part: Math.max(0, Math.min(part, lastPart)),
        digits: 0,
      };
    };

    if (/^\d$/.test(event.key)) {
      const digits = maskPosition.current.digits + 1;
      if (digits >= maskParts[maskPosition.current.part].length) {
        moveTo(maskPosition.current.part + 1);
      } else {
        maskPosition.current = { ...maskPosition.current, digits };
      }
    } else if (event.key === "ArrowRight") {
      moveTo(maskPosition.current.part + 1);
    } else if (event.key === "ArrowLeft") {
      moveTo(maskPosition.current.part - 1);
    } else if (event.key.length === 1 && maskSeparators.includes(event.key)) {
      // Only a half-filled part moves on: the ArrowRight below comes back through here.
      if (maskPosition.current.digits > 0) {
        input.dispatchEvent(
          new KeyboardEvent("keydown", { key: "ArrowRight", bubbles: true })
        );
      }
    } else {
      maskPosition.current = { ...maskPosition.current, digits: 0 };
    }
  };

  const dateTimePickerInputCss = css({
    height,
    width,

    fontFamily: "$openSans !important",
    border: "2px solid $gray-50 !important",

    "&:not(.ant-picker-disabled)": {
      "&:hover": {
        border:
          "2px solid var(--colors-portal-text-input-border-hover) !important",
      },
    },

    "&.ant-picker-focused": {
      border:
        "2px solid var(--colors-portal-text-input-border-focus) !important",
      boxShadow: "0 0 0 2px rgba(189,180,221, 0.1) !important",
    },

    "&.ant-picker-disabled": {
      "&:hover": {
        border: "2px solid $gray-50 !important", // Keep the original border color when disabled
      },
    },
  });

  const dateTimePickerPopupCss = css({
    zIndex: 3100,
    pointerEvents: "all !important",
    fontFamily: "$openSans !important",

    span: {
      fontFamily: "$openSans !important",
    },

    // Add timezone indicator at top of popup — same rule as the suffix above.
    ".ant-picker-panel-container::before": {
      content: showTime ? `"${timezoneLabel}"` : "none",
      display: "block",
      padding: "8px 12px",
      backgroundColor: "#f5f5f5",
      borderBottom: "1px solid #d9d9d9",
      fontSize: "12px",
      color: "#666",
      fontWeight: "500",
      textAlign: "center",
    },

    ".ant-picker-time-panel-column": {
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },

    ".ant-picker-time-panel-cell-selected ": {
      ".ant-picker-time-panel-cell-inner": {
        background:
          "var(--colors-portal-date-picker-popup-time-cell-selected-background) !important",
      },
    },

    ".ant-picker-cell-selected": {
      ".ant-picker-cell-inner": {
        background:
          "var(--colors-portal-date-picker-popup-calendar-cell-selected-background) !important",
      },
    },

    ".ant-picker-time-panel-column::after": {
      height: "0px !important",
    },

    ".ant-picker-cell-today .ant-picker-cell-inner::before": {
      borderColor:
        "var(--colors-portal-date-picker-popup-today-cell-border) !important",
    },

    ".ant-picker-footer": {
      display: hideNowButton ? "none" : "block",

      ".ant-picker-ranges": {
        marginTop: "0",
        fontWeight: "400",

        ".ant-picker-now-btn": {
          color: "#212121",

          "&:hover": {
            opacity: "0.8",
          },
        },

        ".ant-picker-ok": {
          button: {
            "&.ant-btn-primary": {
              backgroundColor:
                "var(--colors-portal-date-picker-popup-ok-button-color) ",

              "&:hover": {
                backgroundColor:
                  "var(--colors-portal-date-picker-popup-ok-button-hover-color) ",
              },
            },
          },
        },
      },
    },
  });

  const disabledDate =
    minDate || maxDate
      ? (d: Dayjs) =>
          !d ||
          (minDate !== undefined && d.isBefore(minDate)) ||
          (maxDate !== undefined && d.isAfter(maxDate, "day"))
      : undefined;

  return (
    <DatePicker
      id={id}
      defaultValue={defaultValue}
      placeholder={enhancedPlaceholder}
      needConfirm={false}
      value={value}
      format={maskFormat ?? displayFormatWithTz}
      onKeyDown={handleMaskKeyDown}
      onFocus={handleMaskFocus}
      onChange={onChange}
      suffixIcon={null}
      classNames={{ popup: { root: dateTimePickerPopupCss() } }}
      className={dateTimePickerInputCss()}
      allowClear
      showTime={
        showTime
          ? {
              format: "HH:mm", // Force 24-hour format
              use12Hours: false, // Disable AM/PM
              defaultValue: defaultTime, // Default time when selecting a date
            }
          : false
      }
      minuteStep={minuteStep}
      disabled={disabled}
      disabledDate={disabledDate}
    />
  );
}
